import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Switch,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Check as CheckIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import {
  useSlackStatus,
  useSlackSettings,
  useSlackChannels,
  useSlackUsers,
  useSlackAuthUrl,
  useDisconnectSlack,
  useTestSlackConnection,
  useUpdateSlackSettings,
} from '../../../hooks/useSlack';
import SlackChannelMapping from './SlackChannelMapping';
import IntegrationKeyManager from './IntegrationKeyManager';
import { usePermissions } from '../../../hooks/usePermissions';
import { useOwnerTitle } from '../../../hooks/useOwnerTitle';
import SetupGuideCard from './SetupGuideCard';
import { slackSetupSteps, getWebhookUrl, docsLinks } from './setupGuides';

const ROLE_OPTIONS = [
  { key: 'ADMIN', label: 'Admin' },
  { key: 'TEACHER', label: 'Teacher' },
];

export default function SlackIntegrationTab() {
  // Connect, disconnect, test, settings and the stored keys are ADMIN only (roles plan 5.6).
  const { isOwner } = usePermissions();
  const ownerTitle = useOwnerTitle();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [localSettings, setLocalSettings] = useState<{
    defaultChannel: string | null;
    notificationMappings: Record<string, string>;
    roleAccess: Record<string, boolean>;
    messageForwardingEnabled: boolean;
  } | null>(null);
  const [settingsDirty, setSettingsDirty] = useState(false);

  // Queries
  const {
    data: status,
    isLoading: statusLoading,
    refetch: refetchStatus,
  } = useSlackStatus();
  const { data: settings } = useSlackSettings();
  const {
    data: channels,
    isLoading: channelsLoading,
    refetch: fetchChannels,
  } = useSlackChannels();
  const { data: linkedUsers } = useSlackUsers();

  // Mutations
  const authUrlMutation = useSlackAuthUrl();
  const disconnectMutation = useDisconnectSlack();
  const testMutation = useTestSlackConnection();
  const updateSettingsMutation = useUpdateSlackSettings();

  // The OAuth result, captured ONCE from the URL: the effect below clears the params right
  // after the first render, and the banners used to read them live, so they vanished (#384).
  const [oauthResult] = useState(() =>
    searchParams.get('tab') === 'slack'
      ? { connected: searchParams.get('connected') === 'true', error: searchParams.get('error') }
      : { connected: false, error: null as string | null }
  );

  // Handle OAuth callback params
  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    const tab = searchParams.get('tab');

    if (tab === 'slack' && (connected || error)) {
      if (connected === 'true') {
        refetchStatus();
      }
      // Clear query params after processing
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('connected');
      newParams.delete('error');
      newParams.delete('tab');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refetchStatus]);

  // Sync settings to local state
  useEffect(() => {
    if (settings && !localSettings) {
      setLocalSettings({
        defaultChannel: settings.defaultChannel,
        notificationMappings: settings.notificationMappings || {},
        roleAccess: settings.roleAccess || { ADMIN: true, TEACHER: true },
        messageForwardingEnabled: settings.messageForwardingEnabled || false,
      });
    }
  }, [settings, localSettings]);

  // Fetch channels when connected. Only the owner sees the channel mapping that uses them,
  // so a non-owner would make a Slack API call per visit that nothing renders.
  useEffect(() => {
    if (isOwner && status?.isConnected) {
      fetchChannels();
    }
  }, [isOwner, status?.isConnected, fetchChannels]);

  // Handlers
  const handleConnect = async () => {
    const result = await authUrlMutation.mutateAsync();
    if (result.authUrl) {
      window.location.href = result.authUrl;
    }
  };

  const handleDisconnect = async () => {
    await disconnectMutation.mutateAsync();
    setShowDisconnectDialog(false);
    setLocalSettings(null);
  };

  const handleTestConnection = () => {
    testMutation.mutate();
  };

  const handleSaveSettings = async () => {
    if (!localSettings) return;
    await updateSettingsMutation.mutateAsync(localSettings);
    setSettingsDirty(false);
  };

  const updateLocalSettings = (
    updates: Partial<NonNullable<typeof localSettings>>
  ) => {
    setLocalSettings((prev) =>
      prev ? { ...prev, ...updates } : null
    );
    setSettingsDirty(true);
  };

  // Loading state
  if (statusLoading) {
    return <Skeleton variant="rectangular" height={400} />;
  }

  const isConnected = status?.isConnected || false;
  const oauthError = oauthResult.error;

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Connect your school's Slack workspace to receive notifications and
        forward messages directly to Slack channels.
      </Typography>

      {/* Setup Guide */}
      <SetupGuideCard
        integration="slack"
        steps={slackSetupSteps}
        webhookUrl={getWebhookUrl('slack')}
        docsLink={docsLinks.slack}
      />

      {/* API Key Manager - REQUIRED before OAuth */}
      {isOwner && <IntegrationKeyManager integration="slack" />}

      {/* OAuth error alert */}
      {oauthError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Connection Failed</AlertTitle>
          {oauthError === 'access_denied'
            ? 'Authorization was denied. Please try again.'
            : oauthError === 'not_permitted'
              ? `Only your ${ownerTitle} can connect the school's Slack workspace.`
              : oauthError === 'invalid_state'
                ? 'That connection link has expired. Please try again.'
                : `Failed to connect to Slack: ${oauthError}`}
        </Alert>
      )}

      {/* OAuth success alert */}
      {oauthResult.connected && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <AlertTitle>Connected Successfully</AlertTitle>
          Your Slack workspace is now connected.
        </Alert>
      )}

      {/* Connection Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={
            <Box sx={{ color: isConnected ? 'primary.main' : 'text.disabled' }}>
              <LinkIcon />
            </Box>
          }
          title="Slack Workspace"
          subheader={
            isConnected
              ? `Connected to ${status?.teamName || 'workspace'}`
              : 'Not connected'
          }
          action={
            isConnected ? (
              <Chip
                icon={<CheckIcon />}
                label="Connected"
                size="small"
                color="success"
              />
            ) : (
              <Chip label="Not Connected" size="small" color="default" />
            )
          }
        />
        <CardContent>
          {isOwner ? (
          <Stack direction="row" spacing={2}>
            {isConnected ? (
              <>
                <Button
                  variant="outlined"
                  onClick={handleTestConnection}
                  disabled={testMutation.isPending}
                  startIcon={
                    testMutation.isPending ? (
                      <CircularProgress size={16} />
                    ) : undefined
                  }
                >
                  Test Connection
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<LinkOffIcon />}
                  onClick={() => setShowDisconnectDialog(true)}
                >
                  Disconnect
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<LinkIcon />}
                onClick={handleConnect}
                disabled={authUrlMutation.isPending}
              >
                {authUrlMutation.isPending
                  ? 'Redirecting...'
                  : 'Connect to Slack'}
              </Button>
            )}
          </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Only your {ownerTitle} can connect, disconnect or configure the school&apos;s Slack workspace.
            </Typography>
          )}

          {isConnected && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Workspace: {status?.teamName || 'Unknown'} &bull; Linked users:{' '}
                {status?.linkedUsersCount || 0}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Settings - only when connected */}
      {isConnected && localSettings && (
        <>
          {isOwner && (
          <>
          {/* General Settings Card */}
          <Card sx={{ mb: 3 }}>
            <CardHeader
              avatar={
                <Box sx={{ color: 'primary.main' }}>
                  <SettingsIcon />
                </Box>
              }
              title="Settings"
            />
            <CardContent>
              <Stack spacing={3}>
                {/* Default Channel */}
                <FormControl fullWidth size="small">
                  <InputLabel>Default Channel</InputLabel>
                  <Select
                    value={localSettings.defaultChannel || ''}
                    label="Default Channel"
                    onChange={(e) =>
                      updateLocalSettings({
                        defaultChannel: e.target.value || null,
                      })
                    }
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {(channels || []).map((ch) => (
                      <MenuItem key={ch.id} value={ch.id}>
                        #{ch.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Message Forwarding Toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={localSettings.messageForwardingEnabled}
                      onChange={(e) =>
                        updateLocalSettings({
                          messageForwardingEnabled: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Forward in-app messages to Slack"
                />

                {/* Role Access */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Roles allowed to link Slack accounts
                  </Typography>
                  {ROLE_OPTIONS.map((role) => (
                    <FormControlLabel
                      key={role.key}
                      control={
                        <Checkbox
                          checked={localSettings.roleAccess[role.key] || false}
                          onChange={(e) =>
                            updateLocalSettings({
                              roleAccess: {
                                ...localSettings.roleAccess,
                                [role.key]: e.target.checked,
                              },
                            })
                          }
                        />
                      }
                      label={role.label}
                    />
                  ))}
                </Box>

                <Divider />

                {/* Channel Mapping */}
                <Typography variant="h6" sx={{ mt: 1 }}>
                  Notification Channel Mapping
                </Typography>
                <SlackChannelMapping
                  notificationMappings={localSettings.notificationMappings}
                  channels={channels || []}
                  channelsLoading={channelsLoading}
                  onChange={(mappings) =>
                    updateLocalSettings({ notificationMappings: mappings })
                  }
                />

                {/* Save Button */}
                <Box>
                  <Button
                    variant="contained"
                    onClick={handleSaveSettings}
                    disabled={
                      !settingsDirty || updateSettingsMutation.isPending
                    }
                    startIcon={
                      updateSettingsMutation.isPending ? (
                        <CircularProgress size={16} />
                      ) : undefined
                    }
                  >
                    {updateSettingsMutation.isPending
                      ? 'Saving...'
                      : 'Save Settings'}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
          </>
          )}

          {/* Linked Users Card */}
          <Card>
            <CardHeader
              avatar={
                <Box sx={{ color: 'primary.main' }}>
                  <PeopleIcon />
                </Box>
              }
              title="Linked Users"
              subheader={`${linkedUsers?.length || 0} users linked`}
            />
            <CardContent>
              {linkedUsers && linkedUsers.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Slack Username</TableCell>
                        <TableCell>Linked</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {linkedUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            {user.firstName} {user.lastName}
                          </TableCell>
                          <TableCell>
                            <Chip label={user.role} size="small" />
                          </TableCell>
                          <TableCell>@{user.slackUsername}</TableCell>
                          <TableCell>
                            {new Date(user.linkedAt).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No users have linked their Slack accounts yet.
                </Typography>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={showDisconnectDialog}
        onClose={() => setShowDisconnectDialog(false)}
      >
        <DialogTitle>Disconnect Slack?</DialogTitle>
        <DialogContent>
          <Typography>
            This will disconnect your Slack workspace and unlink all user
            accounts. Notifications will no longer be sent to Slack.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisconnectDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDisconnect}
            color="error"
            variant="contained"
            disabled={disconnectMutation.isPending}
          >
            {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
